import type { FinanceAttachmentSummary } from '@nadoutong/contracts';
import { Download, FileText } from 'lucide-react';
import { useState } from 'react';
import {
  downloadCreditContractAttachment,
  useCreditContractAttachments,
} from '../api/credit.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { downloadAttachmentPayload } from '../lib/receipts.ts';
import { Button, Skeleton } from '../ui/index.ts';
import { OpsUserRef } from './identity/OpsUserRef.tsx';
import { formatLocalTimestamp } from './formatters.ts';
import { coerceErrorMessage, useToast } from './Toast.tsx';

/**
 * Credit-contract read-back (#1957), shared by Customer 360 and the
 * credit_application approval detail so both reach the SAME bytes through the
 * same endpoint.
 *
 * This is a READ surface. It is deliberately not gated on `canWrite`: the
 * finance manager who signs stage 2 of the application may write nothing on
 * the customer and still has to see the contract before signing.
 *
 * `contractAttachmentKey` narrows the list to the one contract an application
 * actually cites. An application whose key matches nothing renders the
 * "missing" line rather than silently falling back to the newest upload —
 * showing a different document than the one being signed for would be worse
 * than showing none.
 *
 * #1958: the empty line speaks about the FILTER, not about the customer.
 * Filtered and finding nothing means "this application's reference does not
 * resolve" — the customer may well have other contract attachments on file,
 * and telling the stage-2 signer otherwise points them at 驳回 when the right
 * action is to chase the broken reference.
 */

interface CreditContractDownloadProps {
  readonly orgId: string;
  readonly contractAttachmentKey?: string;
  readonly testId: string;
}

export function CreditContractDownload({
  orgId,
  contractAttachmentKey,
  testId,
}: CreditContractDownloadProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { fetchImpl } = useSession();
  const query = useCreditContractAttachments(orgId);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const all = query.data?.attachments ?? [];
  const rows =
    contractAttachmentKey === undefined
      ? all
      : all.filter((attachment) => attachment.object_key === contractAttachmentKey);

  async function handleDownload(attachment: FinanceAttachmentSummary): Promise<void> {
    setDownloadingId(attachment.id);
    try {
      const payload = await downloadCreditContractAttachment(orgId, attachment.id, fetchImpl);
      downloadAttachmentPayload(payload);
    } catch (err) {
      toast.error(coerceErrorMessage(err));
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <section className="flex flex-col gap-2" data-testid={testId}>
      <h3 className="m-0 inline-flex items-center gap-1.5 text-section text-paper">
        <FileText aria-hidden="true" className="h-3.5 w-3.5" />
        {t('credit.contract.title')}
      </h3>
      {query.isPending ? <Skeleton className="h-10 w-full" /> : null}
      {query.isError ? (
        <p className="m-0 text-dense text-bad" data-testid={`${testId}-error`}>
          {coerceErrorMessage(query.error)}
        </p>
      ) : null}
      {query.isSuccess && rows.length === 0 ? (
        <p className="m-0 text-dense text-paper-low" data-testid={`${testId}-empty`}>
          {t(
            contractAttachmentKey === undefined
              ? 'credit.contract.empty'
              : 'credit.contract.empty_filtered',
          )}
        </p>
      ) : null}
      {rows.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {rows.map((attachment) => (
            <li
              key={attachment.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-line-soft bg-well/50 px-3 py-2"
              data-testid={`${testId}-item-${attachment.id}`}
            >
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-dense text-paper">{attachment.file_name}</span>
                <span className="font-mono text-micro text-paper-low">
                  <OpsUserRef id={attachment.uploaded_by_ops_user_id} /> ·{' '}
                  {formatLocalTimestamp(attachment.created_at)}
                </span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                loading={downloadingId === attachment.id}
                onClick={() => void handleDownload(attachment)}
                data-testid={`${testId}-download-${attachment.id}`}
              >
                <Download aria-hidden="true" className="h-3.5 w-3.5" />
                {t('credit.contract.download')}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
